import { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { BarChart3, ShoppingCart, Truck, CreditCard, TrendingUp, Package } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const COLORS = ["#2E7D32", "#F57C00", "#3B82F6", "#4CAF50", "#8B5CF6", "#F59E0B"];
const STATUS_LABELS = { pending: "Pendiente", processing: "Procesando", shipped: "Enviado", delivered: "Entregado" };

const AnalyticsDashboardPage = () => {
  const { user, authAxios } = useAuth();
  const [overview, setOverview] = useState(null);
  const [ordersByMk, setOrdersByMk] = useState([]);
  const [ordersByStatus, setOrdersByStatus] = useState([]);
  const [shipmentsByCarrier, setShipmentsByCarrier] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [ovRes, mkRes, stRes, crRes, tlRes] = await Promise.all([
        authAxios.get("/analytics/overview"),
        authAxios.get("/analytics/orders-by-marketplace"),
        authAxios.get("/analytics/orders-by-status"),
        authAxios.get("/analytics/shipments-by-carrier"),
        authAxios.get("/analytics/revenue-timeline")
      ]);
      setOverview(ovRes.data);
      setOrdersByMk(mkRes.data);
      setOrdersByStatus(stRes.data.map(s => ({ ...s, name: STATUS_LABELS[s.status] || s.status })));
      setShipmentsByCarrier(crRes.data.map(c => ({ ...c, name: c.carrier })));
      setTimeline(tlRes.data);
    } catch (e) { /* error handled */; } finally { setLoading(false); }
  }, [authAxios]);

  useEffect(() => {
    if (user) fetchData();
  }, [user, fetchData]);

  if (!user) return <Navigate to="/login" />;

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FAF9F6]" data-testid="analytics-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#5D4037]">Analítica</h1>
          <p className="text-stone-600">Métricas de tu negocio en tiempo real</p>
        </div>

        {/* KPI Cards */}
        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {[
              { label: "Pedidos", value: overview.total_orders, icon: ShoppingCart, color: "#F57C00" },
              { label: "Envíos", value: overview.total_shipments, icon: Truck, color: "#3B82F6" },
              { label: "Guías", value: overview.total_guides_generated, icon: CreditCard, color: "#8B5CF6" },
              { label: "Ventas MK", value: `$${overview.marketplace_revenue?.toLocaleString("es-MX")}`, icon: TrendingUp, color: "#4CAF50" },
              { label: "Pagos", value: `$${overview.payment_revenue?.toLocaleString("es-MX")}`, icon: Package, color: "#F59E0B" },
              { label: "Total", value: `$${overview.total_revenue?.toLocaleString("es-MX")}`, icon: BarChart3, color: "#2E7D32" },
            ].map((kpi, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="bento-card p-4" data-testid={`kpi-${i}`}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2" style={{ backgroundColor: `${kpi.color}15` }}>
                  <kpi.icon size={16} style={{ color: kpi.color }} />
                </div>
                <p className="text-xl font-black text-[#5D4037]">{kpi.value}</p>
                <p className="text-xs text-stone-500">{kpi.label}</p>
              </motion.div>
            ))}
          </div>
        )}

        {loading ? <div className="text-center py-12">Cargando analítica...</div> : (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Revenue Timeline */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bento-card p-6 lg:col-span-2" data-testid="revenue-chart">
              <h3 className="font-bold text-[#5D4037] mb-4">Ingresos y Pedidos (últimos 7 días)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="revenue" stroke="#2E7D32" strokeWidth={2} name="Ingresos ($)" dot={{ fill: "#2E7D32" }} />
                  <Line yAxisId="right" type="monotone" dataKey="orders" stroke="#F57C00" strokeWidth={2} name="Pedidos" dot={{ fill: "#F57C00" }} />
                  <Line yAxisId="right" type="monotone" dataKey="shipments" stroke="#3B82F6" strokeWidth={2} name="Envíos" dot={{ fill: "#3B82F6" }} />
                </LineChart>
              </ResponsiveContainer>
            </motion.div>

            {/* Orders by Marketplace */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bento-card p-6" data-testid="marketplace-chart">
              <h3 className="font-bold text-[#5D4037] mb-4">Pedidos por Marketplace</h3>
              {ordersByMk.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={ordersByMk}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                    <XAxis dataKey="marketplace" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="orders" fill="#2E7D32" radius={[8, 8, 0, 0]} name="Pedidos" />
                    <Bar dataKey="revenue" fill="#F57C00" radius={[8, 8, 0, 0]} name="Ingresos ($)" />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-stone-400 text-center py-8">Conecta un marketplace para ver datos</p>}
            </motion.div>

            {/* Orders by Status */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bento-card p-6" data-testid="status-chart">
              <h3 className="font-bold text-[#5D4037] mb-4">Estado de Pedidos</h3>
              {ordersByStatus.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={ordersByStatus} cx="50%" cy="50%" outerRadius={90} dataKey="count" nameKey="name" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {ordersByStatus.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-stone-400 text-center py-8">Sin datos de pedidos</p>}
            </motion.div>

            {/* Shipments by Carrier */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bento-card p-6" data-testid="carrier-chart">
              <h3 className="font-bold text-[#5D4037] mb-4">Guías por Paquetería</h3>
              {shipmentsByCarrier.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={shipmentsByCarrier} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={100} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3B82F6" radius={[0, 8, 8, 0]} name="Guías" />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-stone-400 text-center py-8">Genera guías para ver datos</p>}
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboardPage;
